import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

/**
 * "Client · <name>" card, top-right of an affiliate detail page (Figma node
 * 16622:105750).
 *
 * One component for every detail page — lab order, PNOĒ test, service,
 * prescription — so the badge is identical wherever it appears. Only the
 * cross-client History page needs it: the two workspace shells already name the
 * subject in their top bar, so a second copy on the page would be noise.
 *
 * Renders nothing without a name, so a caller can pass whatever it has without
 * guarding.
 */
interface Props {
  firstName?: string | null;
  lastName?: string | null;
  /** Full name, when the caller has it as one string. */
  name?: string | null;
}

const ClientBadge: React.FC<Props> = ({ firstName, lastName, name }) => {
  const theme: Theme = useTheme();

  const fullName = (
    name ?? `${firstName || ""} ${lastName || ""}`
  ).trim();
  if (!fullName) return null;

  const initials = fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <div style={styles.container(theme)}>
      <div style={styles.avatar(theme)}>{initials}</div>
      <div style={styles.textBlock}>
        <div style={styles.label(theme)}>Client</div>
        <div style={styles.name(theme)}>{fullName}</div>
      </div>
    </div>
  );
};

export default ClientBadge;

const styles = {
  container: (theme: Theme) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    boxShadow: "var(--box-shadow-general)",
    flexShrink: 0,
  }),
  avatar: (theme: Theme) => ({
    width: "24px",
    height: "24px",
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: theme.colorTokens.P[20],
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    fontFamily: "var(--font-Bold)",
    fontSize: "11px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  textBlock: {
    display: "flex",
    flexDirection: "column" as "column",
    minWidth: 0,
  },
  label: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  name: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as "nowrap",
  }),
};
